import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import GoodieSquareIcon from "@/design-system/icons/goodie-square-icon.svg";
import TelemetryButton from "@/design-system/telemetry-button";
import { languageSelector, useUserAuthenticationContext } from "@/src/contexts";
import { browserStatics } from "@/src/shared/BrowserStatic";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors } from "../../../styles/tokens.stylex";
import Resources from "./Header.Resources";
import ResourcesEn from "./Header.Resources.En";

export default function HeaderLinks() {
    const resources = languageSelector(Resources, ResourcesEn);
    const user = useUserAuthenticationContext();
    const { pathname } = useLocation();

    let path = pathname;

    if (path.endsWith("/")) {
        path = path.slice(0, -1);
    }

    const links = [
        {
            dataTestId: "hdr-cb",
            children: resources.cashbackLink,
            href: "/cashback",
            telemetryTabName: "CashbackTab",
            icon: undefined as ReactNode,
            isExternal: false,
        },
        {
            dataTestId: "hdr-flyers",
            children: resources.flyersLink,
            href: "/gazetki",
            telemetryTabName: "FlyersTab",
            isExternal: false,
        },
        {
            dataTestId: "hdr-ecards",
            children: resources.eKarty,
            href: "/#eKartyGoodie",
            telemetryTabName: "EKartyTab",
            isExternal: false,
        },
        {
            dataTestId: "hdr-occasions",
            children: resources.offersLink,
            href: "/okazje",
            telemetryTabName: "OffersTab",
            isExternal: false,
        },
    ];

    if (browserStatics.culture === "pl-PL") {
        links.push({
            dataTestId: "hdr-blog",
            children: resources.blogLink,
            href: "/blog",
            telemetryTabName: "BlogTab",
            isExternal: true,
        });
    }

    if (user.canBeMGMSender) {
        links.push({
            dataTestId: "hdr-mgm",
            children: resources.mgmLink,
            href: "/polecaj",
            telemetryTabName: "MgmTab",
            isExternal: false,
        });
    }

    links.push({
        dataTestId: "hdr-plgn",
        children: resources.pluginLink,
        href: "/wtyczka",
        telemetryTabName: "PluginTab",
        isExternal: true,
        icon: <img height={24} width={24} src={GoodieSquareIcon} alt="Wtyczka" />,
    });

    return (
        <nav {...stylex.props(styles.navContainer)}>
            {links.map((link) => (
                <TelemetryButton
                    href={link.href}
                    key={link.href}
                    as="a"
                    size="None"
                    styleX={[styles.link, path === link.href && styles.linkActive]}
                    eventName={`btmbar-${link.telemetryTabName}`}
                    icon={link.icon}
                    data-testid={link.dataTestId}
                    isExternal={link.isExternal}
                >
                    {link.children}
                </TelemetryButton>
            ))}
        </nav>
    );
}

const styles = stylex.create({
    navContainer: {
        gap: ComposableSpacing.s10,

        scrollSnapType: "x mandatory",
        display: "grid",
        gridAutoColumns: "max-content",
        gridAutoFlow: "column",
        gridTemplateRows: "100%",
        position: "relative",

        scrollBehavior: "smooth",

        scrollbarWidth: "none",

        willChange: "scroll-position",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
        maxWidth: "100%",
        overflowX: "scroll",
        overflowY: "hidden",
        paddingBottom: 0,
        paddingLeft: ComposableSpacing.s2,
        paddingRight: ComposableSpacing.s2,
        paddingTop: 0,
        "::-webkit-scrollbar": { display: "none" },
    },
    link: {
        margin: 0,
        borderRadius: 0,
        borderWidth: 0,

        textDecoration: "none",

        color: {
            default: colors.fg_BrandNavy,
            ":hover": colors.b_BrandMagenta,
        },

        fontSize: "1rem",
        fontWeight: 700,
        position: "relative",
        scrollSnapAlign: "center",
        transitionDuration: "0.3s",

        transitionProperty: "border, color",
        borderBottomColor: {
            default: "transparent",
            ":hover": colors.b_BrandMagenta,
        },
        borderBottomStyle: "solid",
        borderBottomWidth: 3,
        paddingBottom: ComposableSpacing.s2,
    },

    linkActive: {
        color: colors.fg_BrandMagenta,
        borderBottomColor: colors.b_BrandMagenta,
    },
});
